/*
 * Every effective limit, with the layer that produced it.
 *
 * The number alone is not actionable and the API says so in its own docstring: two
 * hundred pages is an upgrade conversation when the plan said so and an operator
 * conversation when a scope ceiling said so. So each card shows the effective value,
 * every layer input that produced it, and which one won.
 *
 * Two traps, both of which would render a comfortable-looking lie:
 *  - `null` is unbounded and `0` is a real limit meaning "none allowed", so the
 *    gauge tests for null and never for falsiness;
 *  - `plan_applies: false` means the plan layer does not bind this caller at all (an
 *    admin, a superadmin), which is a different statement from a plan that left a
 *    field unset — and the difference is worth rendering.
 */
import type { KnowledgeBaseLimits, KnowledgeBaseLimit } from '../../../api/knowledgeBases.ts'
import { Empty, Loaded } from '../components/AsyncState.tsx'
import { bytes, num, percent } from '../lib/format.ts'
import { isPressing, metaFor, orderedLimitKeys, sourceLook } from '../lib/limits.ts'
import type { Rates } from '../lib/operations.ts'
import type { Resource } from '../lib/useResource.ts'

interface LimitsTabProps {
  limits: Resource<KnowledgeBaseLimits>
  rates: Rates
}

export function LimitsTab({ limits, rates }: LimitsTabProps) {
  return (
    <div className="pane">
      <Loaded resource={limits} what="лимиты" skeletonRows={5}>
        {(data) => {
          const keys = orderedLimitKeys(data.limits ?? {})
          if (keys.length === 0) {
            return <Empty title="Лимиты не заданы">API не вернул ни одного лимита.</Empty>
          }
          return (
            <>
              <div className="wb__badges" style={{ marginBottom: 16 }}>
                {data.plan_id ? (
                  <span className="pill pill--info pill--sm">план {data.plan_id}</span>
                ) : null}
                {data.plan_source ? (
                  <span className="pill pill--neutral pill--sm">
                    источник плана: {data.plan_source}
                  </span>
                ) : null}
                {data.plan_applies === false ? (
                  <span
                    className="pill pill--accent pill--sm"
                    title="Плановый слой не ограничивает этого пользователя"
                  >
                    план не применяется к вам
                  </span>
                ) : null}
              </div>

              <div className="limits">
                {keys.map((key) => {
                  const limit = data.limits?.[key]
                  if (!limit) return null
                  return (
                    <LimitCard
                      key={key}
                      limitKey={key}
                      limit={limit}
                      used={usedFor(key, data, rates)}
                    />
                  )
                })}
              </div>

              <div className="callout callout--info" style={{ marginTop: 16 }}>
                <b>Почему у каждого лимита указан источник.</b> Действующее значение — это всегда{' '}
                <code>min(план, RC-потолок)</code>, а сверху может лежать персональный или
                пер-базовый override. Когда упираешься в лимит, важно понимать, что именно держит:
                тариф, платформенный потолок или индивидуальная настройка — от этого зависит, к кому
                идти.
              </div>
            </>
          )
        }}
      </Loaded>
    </div>
  )
}

/**
 * The numerator for one limit.
 *
 * `undefined` means the API publishes no counter for it — a ceiling with nothing to
 * measure against — and the card says so instead of drawing an empty bar that reads
 * as "you have used none of it".
 */
function usedFor(key: string, data: KnowledgeBaseLimits, rates: Rates): number | undefined {
  if (key === 'kb_ingests_per_day') return rates.ingestsToday ?? undefined
  if (key === 'kb_ops_per_month') return rates.opsThisMonth ?? undefined
  if (key === 'max_knowledge_bases') return rates.knowledgeBases ?? undefined
  if (key === 'kb_max_concurrent_ops') return rates.concurrentOps ?? undefined
  const meta = metaFor(key)
  if (meta.usage === null) return undefined
  const value = data.usage[meta.usage]
  return typeof value === 'number' ? value : undefined
}

function LimitCard({
  limitKey,
  limit,
  used,
}: {
  limitKey: string
  limit: KnowledgeBaseLimit
  used: number | undefined
}) {
  const meta = metaFor(limitKey)
  const format = meta.unit === 'bytes' ? bytes : num
  const unbounded = limit.value === null || limit.value === undefined
  const look = sourceLook(limit.source)
  const ratio = used === undefined || unbounded ? null : percent(used, limit.value)
  const pressing = used !== undefined && isPressing(used, limit.value)

  return (
    <div className="limit">
      <div className="limit__title">
        {meta.label}
        {meta.crossBase ? (
          <span className="pill pill--neutral pill--sm" title="Считается по всем вашим базам">
            все базы
          </span>
        ) : null}
        {limit.override_value !== null && limit.override_value !== undefined ? (
          <span className="src-tag src-tag--override">override</span>
        ) : null}
      </div>

      <div className={`limit__value${pressing ? ' limit__value--warn' : ''}`}>
        {used !== undefined ? format(used) : unbounded ? '∞' : format(limit.value as number)}
        {used !== undefined ? (
          <small> {unbounded ? '· без лимита' : `/ ${format(limit.value as number)}`}</small>
        ) : unbounded ? (
          <small> без лимита</small>
        ) : (
          <small> максимум</small>
        )}
      </div>

      <div className="meter">
        <i
          style={{
            width: `${ratio ?? 100}%`,
            background: unbounded || ratio === null ? 'rgba(255,255,255,.16)' : meterColour(ratio),
          }}
        />
      </div>

      <div className="limit__src">
        Действует: <span className={`src-tag ${look.className}`}>{look.label}</span>
        <span>{look.recourse}</span>
      </div>

      <div className="limit__layers">
        <span>план: {layerValue(limit.plan_value, format)}</span>
        {limit.floor_value !== null && limit.floor_value !== undefined ? (
          // The offered fail-closed minimum — an input like plan and rc, shown
          // only when the resolver reported one (i.e. no plan was on record).
          <span>минимум: {format(limit.floor_value)}</span>
        ) : null}
        <span>rc: {layerValue(limit.rc_value, format)}</span>
        <span>override: {layerValue(limit.override_value, format)}</span>
      </div>

      {used === undefined && !unbounded ? (
        <p className="field__hint" style={{ marginTop: 7 }}>
          API не отдаёт счётчик для этого лимита — показан только потолок.
        </p>
      ) : null}
      {meta.note ? (
        <p className="field__hint" style={{ marginTop: 7 }}>
          {meta.note}
        </p>
      ) : null}
    </div>
  )
}

function layerValue(
  value: number | null | undefined,
  format: (value: number) => string,
): string {
  if (value === null || value === undefined) return 'не задан'
  return format(value)
}

function meterColour(ratio: number): string {
  if (ratio >= 90) return '#f87171'
  if (ratio >= 80) return '#e0a23f'
  return '#6d9ad4'
}
